import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Play, Pause, RotateCcw, Download, AlertTriangle, CheckCircle, Clock, Database } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Processing() {
  const [processingStatus, setProcessingStatus] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processConfig, setProcessConfig] = useState({
    year: '2022',
    revenueThreshold: '2000000',
    economicDevOnly: true
  });

  const fetchProcessingStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/processing-status`);
      setProcessingStatus(response.data || []);
      
      // Check if any process is currently running
      const hasRunning = response.data?.some(status => status.status === 'processing');
      setIsProcessing(hasRunning);
      
    } catch (error) {
      console.error('Error fetching processing status:', error);
      toast.error('Failed to load processing status');
    } finally {
      setLoading(false);
    }
  };

  const startProcessing = async () => {
    try {
      setIsProcessing(true);
      
      await axios.post(`${API}/process-data`, {
        year: parseInt(processConfig.year),
        revenue_threshold: parseFloat(processConfig.revenueThreshold),
        focus_economic_dev_only: processConfig.economicDevOnly
      });
      
      toast.success(`Started processing IRS 990 data for ${processConfig.year}`);
      
      // Refresh status every few seconds while processing
      const interval = setInterval(() => {
        fetchProcessingStatus();
      }, 3000);
      
      // Stop polling after 2 minutes
      setTimeout(() => {
        clearInterval(interval);
        setIsProcessing(false);
      }, 120000);
      
    } catch (error) {
      console.error('Error starting processing:', error);
      toast.error('Failed to start data processing');
      setIsProcessing(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'failed':
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      case 'processing':
        return <RotateCcw className="h-5 w-5 text-blue-600 animate-spin" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-600" />;
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'failed':
        return 'destructive';
      case 'processing':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getProgressValue = (status) => {
    if (status.status === 'completed') return 100;
    if (status.status === 'failed') return 0;
    if (status.status === 'processing' && status.total_records && status.processed_records) {
      return (status.processed_records / status.total_records) * 100;
    }
    return status.status === 'processing' ? 50 : 0;
  };

  useEffect(() => {
    fetchProcessingStatus();
    
    // Set up polling for active processes
    const interval = setInterval(() => {
      if (processingStatus.some(status => status.status === 'processing')) {
        fetchProcessingStatus();
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight text-slate-900 mb-2">
          IRS Data Processing
        </h1>
        <p className="text-slate-600 max-w-3xl">
          Configure and monitor the processing of IRS 990 forms and EO BMF data. 
          Filter for organizations focused on economic development, entrepreneurship, and business support.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Processing Configuration */}
        <Card className="lg:col-span-1 high-contrast" data-testid="processing-config-card">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center">
              <Database className="h-5 w-5 mr-2" />
              Processing Configuration
            </CardTitle>
            <p className="text-sm text-slate-600">
              Set parameters for IRS data processing
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="year" className="label-text">Tax Year</Label>
              <Select 
                value={processConfig.year} 
                onValueChange={(value) => setProcessConfig(prev => ({ ...prev, year: value }))}
              >
                <SelectTrigger data-testid="year-select">
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2023">2023</SelectItem>
                  <SelectItem value="2022">2022</SelectItem>
                  <SelectItem value="2021">2021</SelectItem>
                  <SelectItem value="2020">2020</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="revenue-threshold" className="label-text">Revenue Threshold</Label>
              <Input
                id="revenue-threshold"
                type="number"
                value={processConfig.revenueThreshold}
                onChange={(e) => setProcessConfig(prev => ({ ...prev, revenueThreshold: e.target.value }))}
                placeholder="Minimum revenue (USD)"
                data-testid="revenue-threshold-input"
              />
              <p className="text-xs text-slate-500">
                Organizations with revenue below this threshold will be excluded
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="economic-dev-only"
                checked={processConfig.economicDevOnly}
                onChange={(e) => setProcessConfig(prev => ({ ...prev, economicDevOnly: e.target.checked }))}
                className="rounded"
                data-testid="economic-dev-checkbox"
              />
              <Label htmlFor="economic-dev-only" className="text-sm">
                Focus on Economic Development Organizations Only
              </Label>
            </div>
            
            <Separator />
            
            <Button 
              onClick={startProcessing}
              disabled={isProcessing}
              className="w-full"
              data-testid="start-processing-btn"
            >
              {isProcessing ? (
                <>
                  <RotateCcw className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Start Processing
                </>
              )}
            </Button>
            
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-sm">
                <p className="font-medium text-blue-900 mb-1">Processing Focus:</p>
                <ul className="text-blue-800 text-xs space-y-1">
                  <li>• Economic development nonprofits</li>
                  <li>• Entrepreneurship & startup support</li>
                  <li>• Microfinance & small business loans</li>
                  <li>• Business development for marginalized communities</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Processing Status */}
        <Card className="lg:col-span-2 high-contrast" data-testid="processing-status-card">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg font-bold">Processing Status</CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchProcessingStatus}
                data-testid="refresh-status-btn"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Refresh
              </Button>
            </div>
            <p className="text-sm text-slate-600">
              Monitor the progress of data processing jobs
            </p>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-24 bg-slate-100 rounded-lg"></div>
                  </div>
                ))}
              </div>
            ) : processingStatus.length > 0 ? (
              <div className="space-y-4">
                {processingStatus.map((status, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-slate-50">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        {getStatusIcon(status.status)}
                        <div>
                          <div className="flex items-center space-x-2">
                            <h3 className="font-medium data-text">
                              {status.process_type?.toUpperCase()} {status.year && `- ${status.year}`}
                            </h3>
                            <Badge variant={getStatusVariant(status.status)}>
                              {status.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">
                            Started: {new Date(status.started_at).toLocaleString()}
                          </p>
                          {status.completed_at && (
                            <p className="text-sm text-slate-600">
                              Completed: {new Date(status.completed_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mb-3">
                      <Progress value={getProgressValue(status)} className="h-2" />
                      <div className="flex justify-between text-xs text-slate-600 mt-1">
                        <span>
                          {status.processed_records || 0} / {status.total_records || '?'} processed
                        </span>
                        <span>{getProgressValue(status).toFixed(0)}%</span>
                      </div>
                    </div>
                    
                    {/* Status Details */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {status.economic_dev_orgs_found !== null && (
                        <div>
                          <span className="text-slate-600">Economic Dev Orgs Found:</span>
                          <span className="font-medium data-text ml-2">
                            {status.economic_dev_orgs_found}
                          </span>
                        </div>
                      )}
                      
                      {status.error_message && (
                        <div className="col-span-2">
                          <span className="text-red-600 font-medium">Error:</span>
                          <p className="text-red-700 text-xs mt-1 bg-red-50 p-2 rounded">
                            {status.error_message}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Database className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Processing Jobs</h3>
                <p className="text-slate-500 mb-4">
                  Start your first IRS data processing job to see status updates here.
                </p>
                <Button 
                  onClick={startProcessing}
                  disabled={isProcessing}
                  data-testid="start-first-processing-btn"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Start Processing
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Information Section */}
      <Card className="mt-6 high-contrast" data-testid="info-section-card">
        <CardHeader>
          <CardTitle className="text-lg font-bold">Data Processing Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h4 className="font-medium text-slate-900 mb-2">Data Sources</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• IRS Form 990 (Annual Return)</li>
                <li>• EO Business Master File (BMF)</li>
                <li>• Schedule B (Major Donors)</li>
                <li>• Financial data and mission statements</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-slate-900 mb-2">Focus Keywords</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Economic development</li>
                <li>• Entrepreneurship, startup support</li>
                <li>• Microfinance, business loans</li>
                <li>• CDFI, business incubators</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-slate-900 mb-2">Output Data</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Organization profiles & missions</li>
                <li>• Donor information & giving patterns</li>
                <li>• Financial data (revenue, assets)</li>
                <li>• Geographic distribution</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
